import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { api, ApiError } from './api';

// ---------------------------------------------------------------- admin session
export interface Admin { email: string; stage: 'password' | 'full'; totpEnabled: boolean }
interface AuthState { admin: Admin | null; loading: boolean; refresh: () => Promise<void> }
const AuthCtx = createContext<AuthState>(null!);
export const useAdmin = () => useContext(AuthCtx);

export function AdminProvider({ children }: { children: ReactNode }) {
  const [admin, setAdmin] = useState<Admin | null>(null);
  const [loading, setLoading] = useState(true);
  const refresh = useCallback(async () => {
    try { setAdmin((await api.get<{ admin: Admin | null }>('/admin/me')).admin); }
    catch { setAdmin(null); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void refresh(); }, [refresh]);
  return <AuthCtx.Provider value={{ admin, loading, refresh }}>{children}</AuthCtx.Provider>;
}

// ---------------------------------------------------------------- data loading
export function useLoad<T>(fn: () => Promise<T>, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const seq = useRef(0);
  const reload = useCallback(async () => {
    const n = ++seq.current;
    setLoading(true);
    try {
      const d = await fn();
      if (n === seq.current) { setData(d); setError(null); }
    } catch (e) {
      if (n === seq.current) setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (n === seq.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(() => { void reload(); }, [reload]);
  return { data, error, loading, reload, setData };
}

/** Wraps an async action with busy and error state for forms and buttons. */
export function useAction<A extends unknown[], R>(fn: (...a: A) => Promise<R>) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [code, setCode] = useState<string | undefined>();
  const run = useCallback(async (...a: A): Promise<R | undefined> => {
    setBusy(true); setError(null); setCode(undefined);
    try { return await fn(...a); }
    catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      if (e instanceof ApiError) setCode(e.code);
      return undefined;
    } finally { setBusy(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fn]);
  return { run, busy, error, code, setError };
}

// ---------------------------------------------------------------- formatting
export const dollars = (c: number) => `$${(c / 100).toFixed(2)}`;
export const fmtDate = (d: string | Date) => new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
export const fmtDateTime = (d: string | Date) => new Date(d).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
export function fmtSecs(s: number | null | undefined) {
  if (s == null) return '';
  const m = Math.floor(s / 60), r = Math.round(s % 60);
  return m ? `${m}:${String(r).padStart(2, '0')}` : `${r}s`;
}
export const plural = (n: number, w: string, ws = `${w}s`) => `${n} ${n === 1 ? w : ws}`;

// ---------------------------------------------------------------- theme: light unless dark is chosen
export type Theme = 'light' | 'dark';
export function useTheme(): [Theme, (t: Theme) => void] {
  const [t, set] = useState<Theme>(() => {
    try { return localStorage.getItem('ml-theme') === 'dark' ? 'dark' : 'light'; } catch { return 'light'; }
  });
  const update = (v: Theme) => {
    set(v);
    try { v === 'dark' ? localStorage.setItem('ml-theme', 'dark') : localStorage.removeItem('ml-theme'); } catch { /* storage blocked */ }
    if (v === 'dark') document.documentElement.dataset.theme = 'dark';
    else delete document.documentElement.dataset.theme;
  };
  return [t, update];
}

export function useTitle(t: string) {
  useEffect(() => { document.title = t ? `${t} · Admin` : 'Math Ladder Admin'; }, [t]);
}

// ---------------------------------------------------------------- paged lists (the API returns rows and a total)
/** One page of a list endpoint. Changing the url (filters, search) goes back to the first page. */
export function usePaged<R>(url: string, key: string, size = 50) {
  const [state, setState] = useState({ url, offset: 0 });
  const offset = state.url === url ? state.offset : 0;
  const sep = url.includes('?') ? '&' : '?';
  const r = useLoad(() => api.get<Record<string, unknown>>(`${url}${sep}limit=${size}&offset=${offset}`), [url, offset]);
  return {
    rows: (r.data?.[key] as R[] | undefined) ?? null,
    total: Number(r.data?.total ?? 0),
    offset,
    setOffset: (o: number) => setState({ url, offset: o }),
    error: r.error, loading: r.loading, reload: r.reload,
  };
}

export const money = (c: number) => `$${(c / 100).toLocaleString(undefined, { minimumFractionDigits: c % 100 ? 2 : 0, maximumFractionDigits: 2 })}`;
export const count = (n: number) => n.toLocaleString();
export const ago = (d: string | Date) => {
  const s = (Date.now() - new Date(d).getTime()) / 1000;
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 86400 * 7) return `${Math.floor(s / 86400)}d ago`;
  return fmtDate(d);
};
